// src/pages/Login.tsx
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { ENABLE_KAKAO } from "../config/social";

// 백엔드 베이스 URL
const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");

export default function Login() {
  const nav = useNavigate();
  const location = useLocation();
  const { user, ready, login } = useAuth();

  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true); // ✅ 기본 ON(원하면 false로)
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // 로그인 이후 중복 내비게이션 방지용 플래그
  const jumped = useRef(false);

  const search = new URLSearchParams(location.search);
  const nextParam = search.get("next") || (location.state as any)?.next || null;

  const computeNext = (isAdmin: boolean) =>
    nextParam || (isAdmin ? "/admin/payment-requests" : "/dashboard");

  // 소셜 로그인 기본 이동 경로 (없으면 /start 로)
  const socialNext = nextParam || "/start";

  // 이미 로그인 상태라면 한 번만 리다이렉트
  useEffect(() => {
    if (!ready) return;
    if (user && !jumped.current) {
      jumped.current = true;
      nav(computeNext(!!user.is_admin), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !pw) return setErr("이메일과 비밀번호를 입력해 주세요.");

    setErr(null);
    setLoading(true);
    try {
      const me = await login(email, pw, remember); // ✅ 여기 반영
      if (!jumped.current) {
        jumped.current = true;
        nav(computeNext(!!me.is_admin), { replace: true });
      }
    } catch (e: any) {
      const msg = String(e?.message || "");
      if (msg.includes("401") || msg.toLowerCase().includes("unauthorized")) {
        setErr("아이디 또는 비밀번호가 잘못되었습니다.");
      } else {
        setErr("로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.");
      }
    } finally {
      setLoading(false);
    }
  }

  // ✅ 카카오 소셜 로그인만 유지
  const handleKakaoLogin = () => {
    if (!ENABLE_KAKAO) return; // ✅ 안전 가드
    const url =
      `${API_BASE}/api/auth/kakao/login?next=` + encodeURIComponent(socialNext);
    window.location.href = url;
  };

  // ✅ 소셜 영역 표시 여부도 카카오만 기준
  const showSocialArea = ENABLE_KAKAO;

  return (
    <div className="min-h-screen bg-white">
      <main className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 py-12 md:grid-cols-2 md:py-16">
        {/* 좌측 간결 문구 */}
        <section className="hidden flex-col justify-center md:flex">
          <div className="mx-auto w-full max-w-md">
            <div className="text-sm font-medium text-[#f97316]">
              주류업 전용 결제 · 증빙
            </div>
            <h1 className="mt-2 text-4xl font-extrabold tracking-tight">
              주류 <span className="text-[#f97316]">pay</span>
            </h1>
            <p className="mt-4 text-slate-600">
              주류 거래 결제부터 증빙 서류 업로드, 관리자 승인까지 한 번에
              처리하세요. 지금 바로 시작해요.
            </p>
          </div>
        </section>

        {/* 우측: 로그인 카드 */}
        <section className="flex items-center">
          <div className="mx-auto w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-[0_18px_45px_rgba(120,63,11,0.16)]">
            <h2 className="mb-1 text-center text-xl font-semibold">로그인</h2>
            <p className="mb-6 text-center text-sm text-slate-500">
              계정이 없으신가요?{" "}
              <Link to="/signup" className="text-[#f97316] underline">
                회원가입
              </Link>
            </p>

            {err && (
              <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 animate-[signupStepIn_.18s_ease-out]">
                {err}
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4">
              <label className="block">
                <span className="mb-1 block text-sm text-slate-700">이메일</span>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    placeholder="아이디를 입력하세요."
                    className="w-full rounded-xl border border-slate-300 px-3 py-3 pl-10 text-sm outline-none transition focus:border-[#f97316] focus:ring-2 focus:ring-[#f97316]/20"
                  />
                  <svg
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path d="M4 6h16v12H4z" stroke="#64748b" strokeWidth="1.5" />
                    <path
                      d="m4 7 8 6 8-6"
                      stroke="#64748b"
                      strokeWidth="1.5"
                      fill="none"
                    />
                  </svg>
                </div>
              </label>

              <label className="block">
                <span className="mb-1 block text-sm text-slate-700">
                  비밀번호
                </span>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    autoComplete="current-password"
                    placeholder="비밀번호를 입력하세요."
                    className="w-full rounded-xl border border-slate-300 px-3 py-3 pl-10 pr-10 text-sm outline-none transition focus:border-[#f97316] focus:ring-2 focus:ring-[#f97316]/20"
                  />
                  <svg
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z"
                      stroke="#64748b"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
                      stroke="#64748b"
                      strokeWidth="1.5"
                    />
                  </svg>
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs text-slate-600 transition hover:bg-slate-100 active:translate-y-px"
                    aria-label="비밀번호 표시"
                  >
                    {showPw ? "숨김" : "표시"}
                  </button>
                </div>
              </label>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-slate-600">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-[#f97316] focus:ring-[#f97316]/50"
                  />
                  로그인 상태 유지
                </label>
                <Link
                  to="/find-account"
                  className="text-[#f97316] hover:underline"
                >
                  아이디/비밀번호 찾기
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary mt-1 w-full text-sm active:translate-y-px disabled:opacity-50"
              >
                {loading ? "로그인 중..." : "로그인"}
              </button>
            </form>

            {/* ✅ 소셜 로그인 영역은 카카오가 켜졌을 때만 표시 */}
            {showSocialArea && (
              <>
                <div className="my-6 flex items-center gap-4">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="text-xs text-slate-400">또는</span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>

                <div className="flex items-center justify-center gap-3">
                  {ENABLE_KAKAO && (
                    <button
                      type="button"
                      onClick={handleKakaoLogin}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FEE500] text-[15px] font-bold text-[#3A1D1D] shadow-sm hover:brightness-105 active:translate-y-px transition"
                      title="Kakao"
                    >
                      K
                    </button>
                  )}
                </div>
              </>
            )}

            <p className="mt-6 text-center text-sm text-slate-500">
              아직 회원이 아니신가요?{" "}
              <Link to="/signup" className="text-[#f97316] underline">
                회원가입하기
              </Link>
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
